"use client";

import { Check, ShieldCheck, Star } from "lucide-react";
import { TrackedNavButton } from "@/components/ui/tracked-nav-button";
import { useButtonTracking } from "@/lib/useButtonTracking";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { Marquee } from "@/components/magicui/marquee";
import { pricingPlans, type PlanFeature } from "@/lib/plans";
import { courses } from "@/app/courses/[slug]/courseData";
import CourseCard from "@/app/courses/components/CourseCard";
// import { createClient } from '@/libs/supabase/client'
import { useRouter, usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import posthog from "posthog-js";
import FAQ from "@/components/home/FAQ";
import ExpenseModal from "@/components/home/ExpenseModal";

interface PricingProps {
  isMobile: boolean;
  isFromSkool?: boolean;
  isLegacyPricing?: boolean;
  headingOverride?: string;
  headingAs?: 'h1' | 'h2';
  popularBadgeOverride?: string;
  extraFeatures?: PlanFeature[];
}

const proofAvatars = [
  "/1_hero/profiles/Frame 33.png",
  "/1_hero/profiles/Frame 34.png",
  "/1_hero/profiles/Frame 35.png",
  "/1_hero/profiles/Frame 36.png",
  "/1_hero/profiles/Frame 37.png",
  "/1_hero/profiles/Frame 38.png",
  "/1_hero/profiles/Frame 39.png",
];

const pricingTestimonials = [
  {
    name: "Ethan Trang",
    role: "AI Engineer & Founder, Nemo AI",
    content:
      "Built an agent system straight from the templates here. It's already making me money on the side.",
  },
  {
    name: "Sarah Johnson",
    role: "Founder, AImpact",
    content:
      "The best, newest tools, news, and techniques already filtered for me. Saved me hours of scrolling Twitter for signal through the noise.",
  },
  {
    name: "Michael Torres",
    role: "CTO, NeuralWorks",
    content:
      "Access to premium AI tools helped us prototype in days instead of weeks. Highly recommend.",
  },
  {
    name: "Priya Nair",
    role: "Indie Hacker",
    content:
      "Shipped my first AI SaaS in a weekend using the Launch Kit. The boilerplates alone saved me weeks.",
  },
  {
    name: "Daniel Kim",
    role: "Product Manager",
    content:
      "I stopped doom-scrolling for AI news. Everything worth knowing lands here already distilled.",
  },
  {
    name: "Lucas Almeida",
    role: "Solo Founder",
    content:
      "The community unblocks me in hours, not days. Worth the membership for the support channel alone.",
  },
];

const featuredCourseIds = [
  "mastering-ai-agents",
  "claude-code-course",
  "openclaw-course",
];
const featuredCourses = featuredCourseIds
  .map((id) => courses.find((c) => c.id === id))
  .filter((c): c is (typeof courses)[number] => c !== undefined);

const avatarColors = ["bg-blue-600", "bg-green-600", "bg-red-600"];
const initials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("");

export default function Pricing({
  isMobile,
  isFromSkool = false,
  isLegacyPricing = false,
  headingOverride,
  headingAs: HeadingTag = 'h2',
  popularBadgeOverride,
  extraFeatures,
}: PricingProps) {
  const { trackButtonClick } = useButtonTracking();
  const router = useRouter();
  const pathname = usePathname();
  const [user, setUser] = useState<User | null>(null);
  const [supabase, setSupabase] = useState<any>(null);
  const [activePlanIndex, setActivePlanIndex] = useState(0);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);

  useEffect(() => {
    // Only create the Supabase client in the browser
    if (typeof window !== "undefined") {
      setSupabase(createClientComponentClient());
    }
  }, []);

  useEffect(() => {
    if (!supabase) return;

    const getUser = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    getUser();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: any, session: any) => {
      setUser(session?.user ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  const handlePurchase = async (plan: string) => {
    const sourcePage = pathname === "/pricing" ? "pricing_page" : "landing_page";
    const sessionId = posthog?.__loaded ? posthog.get_session_id() : null;
    const distinctId = posthog?.__loaded ? posthog.get_distinct_id() : null;

    // Only track if we're on pricing page AND user exists
    if (pathname === "/pricing" && user) {
      trackButtonClick("v2_upgrade_button_clicked", {
        section: "pricing",
        action: "click",
        button_name: `join_${plan.toLowerCase()}`,
        plan: plan,
        source: sourcePage,
        email: user.email,
      });
    }

    const selectedPlan = pricingPlans.find((t) => t.name === plan);
    if (!selectedPlan) return;

    // Auto-apply a promo code passed via URL (e.g. /pricing?promo=NEWSLETTER50)
    const promoCode =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("promo") || undefined
        : undefined;

    // Select the appropriate priceId based on source
    let priceId = selectedPlan.priceId; // Default price
    if (isLegacyPricing && selectedPlan.legacyPriceId) {
      priceId = selectedPlan.legacyPriceId; // Legacy takes precedence
    } else if (isFromSkool && selectedPlan.skoolPriceId) {
      priceId = selectedPlan.skoolPriceId; // Skool special price
    }

    try {
      // Create checkout session
      const response = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          email: user?.email,
          promoCode,
          metadata: {
            sessionId,
            distinctId,
            sourcePage,
            plan,
            isFromSkool,
            isLegacyPricing
          }
        }),
      });

      const { url } = await response.json();

      // Always redirect to checkout page directly
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert("Payment processing error, please try again, or contact service@aibuilderclub.com for help")
    }
  };

  const getMonthlyPrice = (planName: string) => {
    if (isLegacyPricing) return planName === "Monthly" ? "19" : "15";
    if (isFromSkool) return planName === "Monthly" ? "26" : "17";
    return planName === "Monthly" ? "37" : "24";
  };

  const getValueTotal = (
    features: { value?: string }[]
  ) =>
    features.reduce((sum, f) => {
      if (!f.value) return sum;
      const n = Number(f.value.replace(/[^0-9.]/g, ""));
      return sum + (isNaN(n) ? 0 : n);
    }, 0);

  const nextPlan = () => {
    setActivePlanIndex((prev) => (prev + 1) % pricingPlans.length);
  };

  const prevPlan = () => {
    setActivePlanIndex(
      (prev) => (prev - 1 + pricingPlans.length) % pricingPlans.length
    );
  };

  return (
    <>
      <section className="py-12 md:py-20 section-container" id="pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8">
            <HeadingTag className="text-3xl sm:text-5xl md:text-6xl font-bold mb-6 text-balance break-words">
              {isLegacyPricing
                ? "AI Builder Club: Legacy Member Offer"
                : isFromSkool
                  ? "AI Builder Club: Skool Member Offer"
                  : headingOverride ?? "Ship your first AI app in 30 days"}
            </HeadingTag>
            <p className="text-lg sm:text-xl md:text-2xl text-white/70 mb-8 text-balance">
              {isLegacyPricing
                ? "Exclusive for Legacy members: 3 month 30% off + 1 additional month free"
                : isFromSkool 
                  ? "Exclusive for Skool members: 3 month 30% off + 1 additional month free" 
                  : "Join 1,281 builders learning to ship real AI apps. Money back in 30 days if it's not for you."}
            </p>

            {/* Social proof / trust bar */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-2">
              <div className="flex items-center">
                {proofAvatars.map((src, i) => (
                  <div
                    key={i}
                    className="relative w-9 h-9 rounded-full overflow-hidden border-2 border-black -ml-2 first:ml-0"
                    style={{ zIndex: proofAvatars.length - i }}
                  >
                    <Image src={src} alt={`Member ${i + 1}`} fill className="object-cover" />
                  </div>
                ))}
                <span className="ml-3 text-sm text-white/70">1,281 builders</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex">
                  {Array(5)
                    .fill(0)
                    .map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    ))}
                </div>
                <span className="text-sm text-white/70">4.9/5 from members</span>
              </div>
            </div>
          </div>

          <div className={`${isMobile ? "block" : "grid grid-cols-1 md:grid-cols-2 gap-8"} max-w-6xl mx-auto`}>
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`glass-card p-8 rounded-3xl relative flex flex-col h-full ${
                  plan.popular ? "bg-white text-black" : "bg-[#1A1A1A]"
                } ${isMobile ? "mb-6" : ""}`}
              >
                {/* Most popular badge */}
                {plan.popular && (
                  <div className="absolute top-0 right-0 mt-4 mr-4">
                    <span className={`bg-black text-white ${
                      isMobile ? "text-xs" : "text-sm"
                    } font-medium px-4 py-2 rounded-full`}>
                      Most popular
                    </span>
                  </div>
                )}

                {/* Plan name - removed mt-8 to fix spacing */}
                <h3 className={`${isMobile ? "text-xl" : "text-2xl"} font-bold mb-4`}>
                  {plan.name}
                </h3>

                <div className="mb-8">
                  <div className="flex items-baseline mb-2">
                    <span className={`${isMobile ? "text-3xl" : "text-4xl"} font-bold`}>$</span>
                    <span className={`${isMobile ? "text-4xl" : "text-5xl"} font-bold`}>
                      {getMonthlyPrice(plan.name)}
                    </span>
                    <span className={`ml-1 ${isMobile ? "text-base" : "text-lg"}`}>/month</span>
                  </div>
                  <p className={`${isMobile ? "text-sm" : "text-base"} ${
                    plan.popular ? "text-black/70" : "text-white/70"
                  }`}>
                    {plan.name === "Monthly" ? (
                      isLegacyPricing
                        ? "30% off for 3 months, and $27/month after 3 months"
                        : isFromSkool 
                          ? "30% off for 3 months, and $37/month after 3 months" 
                          : "Get started with essentials"
                    ) : (
                      <span className="flex items-center gap-2">
                        {isLegacyPricing
                          ? "$180 billed annually"
                          : isFromSkool 
                            ? "$204 billed annually" 
                            : "$289 billed annually"}
                        <span className="bg-green-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                          {isLegacyPricing
                            ? "Save 45%"
                            : isFromSkool
                              ? "Save 45%"
                              : popularBadgeOverride ?? "Save 35%"}
                        </span>
                      </span>
                    )}
                  </p>
                </div>

                {plan.popular && (isFromSkool || isLegacyPricing) && (
                  <p className={`font-medium mb-4 ${isMobile ? "text-sm" : ""}`}>
                    {isLegacyPricing ? "Legacy Member Benefits:" : "Skool Member Benefits:"}
                  </p>
                )}

                {plan.featuresIntro && (
                  <p className={`font-semibold mb-3 ${isMobile ? "text-sm" : "text-base"} ${
                    plan.popular ? "text-black" : "text-white"
                  }`}>
                    {plan.featuresIntro}
                  </p>
                )}

                {getValueTotal(plan.features) > 0 && (
                  <div className={`flex items-center justify-between mb-3 text-xs uppercase tracking-wide ${
                    plan.popular ? "text-black/50" : "text-white/50"
                  }`}>
                    <span>What you get</span>
                    <span>Value</span>
                  </div>
                )}

                <ul className="space-y-4 mb-8 flex-grow">
                  {(plan.popular && extraFeatures
                    ? [...plan.features, ...extraFeatures]
                    : plan.features
                  ).map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-2">
                      <Check className={`h-5 w-5 shrink-0 ${
                        plan.popular ? "text-black" : "text-white"
                      }`} />
                      <span className={`flex-grow ${isMobile ? "text-sm" : "text-base"} ${
                        plan.popular ? "text-black/80" : "text-white/80"
                      }`}>
                        {feature.href ? (
                          <Link
                            href={feature.href}
                            className="underline underline-offset-2 hover:opacity-80"
                          >
                            {feature.text}
                          </Link>
                        ) : (
                          feature.text
                        )}
                      </span>
                      {feature.value && (
                        <span className={`shrink-0 line-through ${isMobile ? "text-xs" : "text-sm"} ${
                          plan.popular ? "text-black/40" : "text-white/40"
                        }`}>
                          {feature.value}
                        </span>
                      )}
                    </li>
                  ))}
                  <li
                    className="flex items-center cursor-pointer group"
                    onClick={() => setIsExpenseModalOpen(true)}
                    role="button"
                    aria-label="Open reimbursement details"
                  >
                    <Check className={`h-5 w-5 mr-3 ${
                      plan.popular ? "text-black" : "text-white"
                    } group-hover:opacity-80`} />
                    <span className={`${isMobile ? "text-sm" : "text-base"} ${
                      plan.popular ? "text-black/80" : "text-white/80"
                    } group-hover:opacity-80`}
                    >
                      Company reimbursement ready <span className="ml-1 opacity-70">→</span>
                    </span>
                  </li>
                </ul>

                {(plan.valueTotal ?? getValueTotal(plan.features)) > 0 && (
                  <div className={`mb-5 rounded-2xl px-4 py-3 ${
                    plan.popular ? "bg-black/5" : "bg-white/5"
                  }`}>
                    <div className={`flex items-center justify-between ${isMobile ? "text-sm" : "text-base"} ${
                      plan.popular ? "text-black/60" : "text-white/60"
                    }`}>
                      <span>Total value</span>
                      <span className="line-through">
                        ${(plan.valueTotal ?? getValueTotal(plan.features)).toLocaleString()}
                      </span>
                    </div>
                    <div className={`flex items-center justify-between font-bold ${isMobile ? "text-base" : "text-lg"} ${
                      plan.popular ? "text-black" : "text-white"
                    }`}>
                      <span>Your price</span>
                      <span>${getMonthlyPrice(plan.name)}/mo</span>
                    </div>
                  </div>
                )}

                <TrackedNavButton
                  onClick={() => handlePurchase(plan.name)}
                  className={`w-full py-3 px-8 rounded-full font-medium transition-colors inline-flex items-center justify-center group text-base ${
                    plan.popular
                      ? "bg-black text-white hover:bg-black/90"
                      : "bg-white text-black hover:bg-white/90"
                  }`}
                >
                  <span>{plan.ctaLabel ?? `Join ${plan.name} Plan`}</span>
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                </TrackedNavButton>

                <div className={`flex items-center justify-center gap-1.5 mt-3 text-xs ${
                  plan.popular ? "text-black/60" : "text-white/60"
                }`}>
                  <ShieldCheck className="h-4 w-4 shrink-0" />
                  <span>30-day money-back guarantee</span>
                </div>
              </div>
            ))}
          </div>

          {/* Social proof: member testimonials sliding marquee */}
          <div className="mt-12 relative w-full overflow-hidden">
            <Marquee pauseOnHover className="[--duration:40s] [--gap:1.5rem]">
              {pricingTestimonials.map((t, i) => (
                <div
                  key={i}
                  className="w-72 sm:w-80 shrink-0 bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 text-left flex flex-col justify-between"
                >
                  <div>
                    <div className="flex mb-3">
                      {Array(5)
                        .fill(0)
                        .map((_, s) => (
                          <Star key={s} className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        ))}
                    </div>
                    <p className="text-sm text-white/85 leading-relaxed mb-5">
                      &ldquo;{t.content}&rdquo;
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 rounded-full ${avatarColors[i % avatarColors.length]} flex items-center justify-center shrink-0`}
                    >
                      <span className="text-white text-xs font-bold">{initials(t.name)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{t.name}</p>
                      <p className="text-xs text-white/50">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </Marquee>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-1/6 bg-gradient-to-r from-black" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-1/6 bg-gradient-to-l from-black" />
          </div>

          {/* Featured courses */}
          <div className="mt-16 max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h3 className={`${isMobile ? "text-3xl" : "text-4xl"} font-bold mb-3`}>
                Featured courses
              </h3>
              <p className={`${isMobile ? "text-base" : "text-lg"} text-white/70`}>
                Unlimited access to all 15+ courses, included with every plan.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredCourses.map((course, index) => (
                <CourseCard key={course.id} course={course} priority={index < 3} />
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                href="/courses"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-4"
              >
                View all courses
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className={`mt-12 text-center ${isMobile ? "text-sm" : ""}`}>
            <div className="space-y-2">
              <p className="text-white/70">
                <button
                  onClick={() => setIsExpenseModalOpen(true)}
                  className="underline hover:text-white"
                >Expense it</button> (annual) · Students 50% off · Teams 33% off (5+ seats)
              </p>
              <p className="text-white/70">
                Email{" "}
                <a href="mailto:service@aibuilderclub.com" className="underline">
                  service@aibuilderclub.com
                </a>{" "}
                to access the discount or for any questions.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section integrated directly within the Pricing component */}
      <FAQ isMobile={isMobile} />

      {/* Expense Modal */}
      <ExpenseModal 
        isOpen={isExpenseModalOpen} 
        onClose={() => setIsExpenseModalOpen(false)} 
      />
    </>
  );
}
